import React, { useCallback } from 'react'
import { x } from '@xstyled/styled-components'
import { Field, FormikProps, FormikValues } from 'formik'
import getOr from 'lodash/fp/getOr'
import Button from '@mailstep/design-system/ui/Elements/Button'
import ErrorMessage from '@mailstep/design-system/ui/Elements/ErrorMessage'
import { Trans } from '@lingui/react'
import InputField from '@components/forms/components/InputField'
import SelectField from '@components/forms/components/SelectField'
import { FieldLabel } from '@mailstep/design-system/ui/Elements/Label'
import { Product } from '@typings/entities/Product'
import SwitchField from '@components/forms/components/SwitchField'
import { nanoid } from 'nanoid'
import { codeTypeOptions } from '../constants'
import { ToastError, ToastSuccess } from '@mailstep/design-system'
import { H7 } from '@mailstep/design-system/ui/Elements/Typography'

type Props = {
  remove: Function
  push: Function
  name: string
  form: FormikProps<FormikValues>
  canEdit: boolean
  canRemove: boolean
  canAdd: boolean
  icon?: string
  label?: React.ReactNode
  min?: number
}

const ProductCodesField = ({ remove, push, label, name, form, canEdit, canRemove, canAdd, icon, min }: Props): JSX.Element => {
  const codes: Product['codes'] = getOr([], name, form?.values)
  const errors = form.errors?.[name] && typeof form.errors?.[name] === 'string' ? form.errors?.[name] : null
  const addAnother = useCallback(() => push({ id: nanoid(), primary: true }), [push])

  return (
    <x.div>
      <FieldLabel htmlFor={name}>{label}</FieldLabel>

      <x.div>
        {codes?.map((code, index) => (
          <x.div display="flex" gap="8px" key={`${code.id}`}>
            <Field
              component={InputField}
              value={code?.value}
              name={`${name}[${index}].value`}
              label={<Trans id="form.code.label" message="Code" />}
              disabled={!canEdit && !!code?.createdAt}
              icon={icon}
              spaceAround
              id={index}
              alwaysShowClear
            />
            <Field
              component={SelectField}
              value={code?.type}
              name={`${name}[${index}].type`}
              label={<Trans id="form.type.label" message="Type" />}
              options={codeTypeOptions}
              disabled={!canEdit && !!code?.createdAt}
              spaceAround
            />

            <x.div mt="6px">
              <Field
                component={SwitchField}
                value={code?.primary}
                name={`${name}[${index}].primary`}
                label={<Trans id="form.primary.label" message="Primary" />}
                disabled={!canEdit && !!code?.createdAt}
                labelJustify="between"
              />
            </x.div>

            <x.div display="flex" flexDirection="column" alignItems="center">
              <H7 variant="semiBold" mt="6px">
                <Trans id="form.active.label" message="Active" />
              </H7>

              {code.active ? <ToastSuccess /> : <ToastError />}
            </x.div>

            <x.div mt="24px">
              <Button
                type="button"
                disabled={(!canRemove && !!code?.createdAt) || (!!min && codes.length <= min)}
                onClick={() => remove(index)}
                icon="deleteCan"
                appearance="ghost"
              />
            </x.div>
          </x.div>
        ))}

        {errors && (
          <x.div mb="8px">
            <ErrorMessage>{errors}</ErrorMessage>
          </x.div>
        )}
      </x.div>

      <x.div mb="16px">
        <Button type="button" onClick={addAnother} icon="plus" appearance="ghost" disabled={!canAdd}></Button>
      </x.div>
    </x.div>
  )
}

export default ProductCodesField
